/**
 * CreatorContentStore - Loads and manages content from Creator CMS.
 *
 * Responsibilities:
 * 1. Load creator data from a URL (via CreatorApi)
 * 2. Transform to internal Data format (via CreatorMapper)
 * 3. Manage state and provide content lookup
 */

import type { Data } from "@core/content/types";
import {
  mapCreatorPayloadToContent,
  mapCreatorPayloadToMicrositeData,
} from "./CreatorMapper";
import { loadCreatorCollection } from "./CreatorApi";
import type { CreatorPayload, MicrositeData } from "./types";

// =============================================================================
// TYPES
// =============================================================================

export interface CreatorContentStoreOptions {
  creatorDataSource: string;
  manualContent?: Data[];
  /** Pre-loaded payload from SSR — bypasses network fetch when provided. */
  injectedPayload?: CreatorPayload;
}

export interface CreatorContentStoreState {
  contentData: Data[];
  micrositeData: MicrositeData;
  initialContentId: string;
  creatorPayload: CreatorPayload | null;
}

export interface CreatorContentPipeline {
  configure: (options: CreatorContentStoreOptions) => void;
  initializeContentData: () => Promise<void>;
  resolveDeferredContentData: () => Promise<void>;
  getContentData: () => Data[];
  getMicrositeData: () => MicrositeData;
  getInitialContentId: () => string;
  getContent: (id: string) => Data | undefined;
}

// =============================================================================
// CONTENT LOOKUP HELPERS
// =============================================================================

export const toTargetNames = (name: string | string[]): string[] =>
  Array.isArray(name) ? name : [name];

export const getContentByIdOrTarget = (contentData: Data[], id: string): Data | undefined => {
  return contentData.find(
    (item) =>
      item.id === id || (item.target && toTargetNames(item.target.targetName).includes(id))
  );
};

// =============================================================================
// STORE FACTORY
// =============================================================================

const createInitialState = (manualContent: Data[]): CreatorContentStoreState => ({
  contentData: [...manualContent],
  micrositeData: {
    name: "",
    description: "",
    constructedCover: "",
  },
  initialContentId: "",
  creatorPayload: null,
});

export const createCreatorContentStore = (options: CreatorContentStoreOptions) => {
  const manualContent = options.manualContent ?? [];
  let state = createInitialState(manualContent);
  let deferredContentResolved = false;

  const initialize = async (): Promise<CreatorContentStoreState> => {
    try {
      const creatorPayload = options.injectedPayload
        ?? await loadCreatorCollection(options.creatorDataSource);
      const creatorContent = mapCreatorPayloadToContent(creatorPayload);

      state = {
        contentData: [...creatorContent, ...manualContent],
        micrositeData: mapCreatorPayloadToMicrositeData(creatorPayload),
        initialContentId: creatorContent[0]?.id ?? "",
        creatorPayload,
      };
      deferredContentResolved = false;

      return state;
    } catch (error) {
      console.error("Unable to load creator data:", options.creatorDataSource, error);
      state = createInitialState(manualContent);
      deferredContentResolved = false;
      return state;
    }
  };

  const resolveDeferredContent = async (): Promise<CreatorContentStoreState> => {
    if (deferredContentResolved) return state;
    const hasPanelRefs = state.contentData.some((item) =>
      item.content?.some((contentItem) => contentItem.type === "panels")
    );
    if (!hasPanelRefs) {
      deferredContentResolved = true;
      return state;
    }
    const { resolvePanelsInContent } = await import("./PanelResolver");
    state = {
      ...state,
      contentData: await resolvePanelsInContent(state.contentData),
    };
    deferredContentResolved = true;
    return state;
  };

  return {
    initialize,
    resolveDeferredContent,
    getState: () => state,
    getContent: (id: string) => getContentByIdOrTarget(state.contentData, id),
  };
};

/**
 * Shared app-facing pipeline wrapper around CreatorContentStore.
 * Keeps project-level files limited to configuration (source URL + manual content).
 */
export const createCreatorContentPipeline = (
  options: CreatorContentStoreOptions
): CreatorContentPipeline => {
  let creatorContentStore = createCreatorContentStore(options);

  const configure = (nextOptions: CreatorContentStoreOptions): void => {
    creatorContentStore = createCreatorContentStore(nextOptions);
  };

  const initializeContentData = async (): Promise<void> => {
    await creatorContentStore.initialize();
  };

  const resolveDeferredContentData = async (): Promise<void> => {
    await creatorContentStore.resolveDeferredContent();
  };

  const getContentData = (): Data[] => creatorContentStore.getState().contentData;

  const getMicrositeData = (): MicrositeData => creatorContentStore.getState().micrositeData;

  const getInitialContentId = (): string => creatorContentStore.getState().initialContentId;

  const getContent = (id: string): Data | undefined => creatorContentStore.getContent(id);

  return {
    configure,
    initializeContentData,
    resolveDeferredContentData,
    getContentData,
    getMicrositeData,
    getInitialContentId,
    getContent,
  };
};
